import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { formatDistanceToNow, differenceInDays } from 'date-fns';
import {
  AlertTriangle, Brain, MessageSquare, BookOpen, TrendingDown,
  RefreshCw, Zap, ArrowRight, CheckCircle2, Clock,
} from 'lucide-react';

// ── Types ─────────────────────────────────────────────────────────

interface OrgRow {
  id: string;
  name: string;
  status: string;
  created_at: string;
  knowledge_store_complete: boolean;
}

interface HealthFlag {
  id: string;
  org_id: string;
  flag_type: string;
  severity: string;
  created_at: string;
}

interface ThinkingSession {
  id: string;
  org_id: string;
  status: string;
  trigger_type: string;
  created_at: string;
}

interface CommsPackage {
  org_id: string;
  status: string;
  generated_at: string;
}

interface ActionReason {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  detail: string;
  score: number;
  cta?: { label: string; path: string };
}

interface OrgAction {
  org_id: string;
  org_name: string;
  org_status: string;
  totalScore: number;
  reasons: ActionReason[];
}

// ── Scoring constants ─────────────────────────────────────────────

const SCORE = {
  HIGH_FLAG:            10,
  MEDIUM_FLAG:          5,
  THINKING_PENDING:     7,
  COMMS_GAP:            6,
  KNOWLEDGE_INCOMPLETE: 4,
  LOW_ACTIVATION:       6,
  DROPPING_VELOCITY:    8,
};

// ── Helpers ───────────────────────────────────────────────────────

function urgencyColor(score: number) {
  if (score >= 20) return { badge: 'bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/30', dot: 'bg-red-500' };
  if (score >= 12) return { badge: 'bg-orange-500/10 text-orange-700 dark:text-orange-400 border-orange-500/30', dot: 'bg-orange-500' };
  if (score >= 6)  return { badge: 'bg-yellow-500/10 text-yellow-700 dark:text-yellow-400 border-yellow-500/30', dot: 'bg-yellow-400' };
  return                  { badge: 'border-muted text-muted-foreground', dot: 'bg-muted-foreground' };
}

function urgencyLabel(score: number) {
  if (score >= 20) return 'Critical';
  if (score >= 12) return 'High';
  if (score >= 6)  return 'Medium';
  return 'Low';
}

// ── Main component ────────────────────────────────────────────────

export default function NextBestAction() {
  const navigate = useNavigate();

  const [actions, setActions]       = useState<OrgAction[]>([]);
  const [loading, setLoading]       = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const fourteenDaysAgo = new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString();

      const [orgsRes, flagsRes, thinkingRes, commsRes, orgDataRes] = await Promise.all([
        supabase.from('organizations').select('id, name, status, created_at, knowledge_store_complete').order('name'),
        supabase.from('org_health_flags').select('id, org_id, flag_type, severity, created_at').is('resolved_at', null),
        supabase.from('cosmo_thinking_sessions').select('id, org_id, status, trigger_type, created_at').eq('status', 'draft'),
        // Recent approved or sent comms packages
        supabase
          .from('council_comms_packages')
          .select('org_id, status, generated_at')
          .in('status', ['approved', 'sent'])
          .gt('generated_at', fourteenDaysAgo),
        supabase.rpc('get_all_orgs_admin'),
      ]);

      const orgs: OrgRow[]             = orgsRes.data    || [];
      const flags: HealthFlag[]        = flagsRes.data   || [];
      const thinking: ThinkingSession[]= thinkingRes.data|| [];
      const comms: CommsPackage[]      = commsRes.data   || [];
      const orgStats: any[]            = orgDataRes.data || [];

      // Build maps
      const flagsByOrg   = new Map<string, HealthFlag[]>();
      const thinkByOrg   = new Map<string, ThinkingSession[]>();
      const commsOrgSet  = new Set(comms.map(c => c.org_id));
      const statsMap     = new Map(orgStats.map((o: any) => [o.org_id, o]));

      for (const f of flags) {
        if (!flagsByOrg.has(f.org_id)) flagsByOrg.set(f.org_id, []);
        flagsByOrg.get(f.org_id)!.push(f);
      }
      for (const t of thinking) {
        if (!thinkByOrg.has(t.org_id)) thinkByOrg.set(t.org_id, []);
        thinkByOrg.get(t.org_id)!.push(t);
      }

      const computed: OrgAction[] = [];

      for (const org of orgs) {
        const reasons: ActionReason[] = [];
        const orgFlags  = flagsByOrg.get(org.id) || [];
        const orgThink  = thinkByOrg.get(org.id) || [];
        const stats     = statsMap.get(org.id);

        // ── High severity flags ─────────────────────────────────────
        const highFlags = orgFlags.filter(f => f.severity === 'high');
        if (highFlags.length > 0) {
          reasons.push({
            icon: AlertTriangle,
            label: `${highFlags.length} high-severity health flag${highFlags.length > 1 ? 's' : ''}`,
            detail: highFlags.map(f => f.flag_type.replace(/_/g, ' ')).slice(0, 2).join(', '),
            score: highFlags.length * SCORE.HIGH_FLAG,
            cta: { label: 'View org', path: `/admin/org/${org.id}` },
          });
        }

        // ── Medium severity flags ───────────────────────────────────
        const medFlags = orgFlags.filter(f => f.severity === 'medium');
        if (medFlags.length > 0) {
          reasons.push({
            icon: AlertTriangle,
            label: `${medFlags.length} medium-severity flag${medFlags.length > 1 ? 's' : ''}`,
            detail: medFlags.map(f => f.flag_type.replace(/_/g, ' ')).slice(0, 2).join(', '),
            score: medFlags.length * SCORE.MEDIUM_FLAG,
          });
        }

        // ── Pending thinking sessions ───────────────────────────────
        if (orgThink.length > 0) {
          const oldest = orgThink.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())[0];
          const daysOld = differenceInDays(new Date(), new Date(oldest.created_at));
          reasons.push({
            icon: Brain,
            label: `${orgThink.length} thinking session${orgThink.length > 1 ? 's' : ''} pending review`,
            detail: `Oldest waiting ${daysOld} day${daysOld !== 1 ? 's' : ''} · trigger: ${oldest.trigger_type.replace(/_/g, ' ')}`,
            score: orgThink.length * SCORE.THINKING_PENDING,
            cta: { label: 'Review thinking', path: `/admin/growth/thinking/${org.id}?session=${oldest.id}` },
          });
        }

        // ── Comms gap: no approved package in 14 days ──────────────
        if (!commsOrgSet.has(org.id) && org.status !== 'paused') {
          reasons.push({
            icon: MessageSquare,
            label: 'No approved comms in 14 days',
            detail: 'Council hasn\'t sent a package recently',
            score: SCORE.COMMS_GAP,
            cta: { label: 'View org', path: `/admin/org/${org.id}` },
          });
        }

        // ── Knowledge store incomplete ──────────────────────────────
        if (!org.knowledge_store_complete) {
          const daysOld = differenceInDays(new Date(), new Date(org.created_at));
          if (daysOld > 7) {
            reasons.push({
              icon: BookOpen,
              label: 'Knowledge store not set up',
              detail: `Org created ${daysOld} days ago — Cosmo is flying blind`,
              score: SCORE.KNOWLEDGE_INCOMPLETE,
              cta: { label: 'Set up knowledge', path: `/admin/org/${org.id}?tab=knowledge` },
            });
          }
        }

        // ── Low activation (< 40% and > 5 users) ───────────────────
        if (stats && stats.total_users > 5) {
          const activationPct = Math.round((stats.active_users / stats.total_users) * 100);
          if (activationPct < 40) {
            reasons.push({
              icon: TrendingDown,
              label: `Low activation — ${activationPct}% active`,
              detail: `${stats.active_users} of ${stats.total_users} users active this week`,
              score: SCORE.LOW_ACTIVATION,
            });
          }

          // ── Dropping velocity (this week vs estimated prior week) ──
          const priorWeekEst = Math.max(stats.scored_30d - stats.scored_7d, 0) / 3.3;
          const ratio = priorWeekEst > 1 ? stats.scored_7d / priorWeekEst : 0;
          if (ratio < 0.5 && stats.scored_30d > 10) {
            reasons.push({
              icon: TrendingDown,
              label: 'Activity dropping fast',
              detail: `Only ${Math.round(ratio * 100)}% of expected weekly activity`,
              score: SCORE.DROPPING_VELOCITY,
            });
          }
        }

        if (reasons.length === 0) continue;

        const totalScore = reasons.reduce((sum, r) => sum + r.score, 0);
        computed.push({
          org_id:     org.id,
          org_name:   org.name,
          org_status: org.status,
          totalScore,
          reasons,
        });
      }

      // Sort by urgency score descending
      computed.sort((a, b) => b.totalScore - a.totalScore);

      setActions(computed);
      setLastRefreshed(new Date());
    } catch (err) {
      console.error('NextBestAction load error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-64">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Zap className="h-5 w-5 animate-pulse" />
          <span className="text-sm">Computing priorities…</span>
        </div>
      </div>
    );
  }

  const thisWeek = new Date();
  const weekStr  = thisWeek.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });

  return (
    <div className="p-6 space-y-6 max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Next Best Actions</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Week of {weekStr} · {actions.length} org{actions.length !== 1 ? 's' : ''} need attention
            {lastRefreshed && ` · updated ${formatDistanceToNow(lastRefreshed, { addSuffix: true })}`}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Priority list */}
      {actions.length === 0 ? (
        <Card className="border-green-500/20 bg-green-500/5">
          <CardContent className="py-10 text-center">
            <CheckCircle2 className="h-8 w-8 text-green-500 mx-auto mb-3" />
            <p className="font-medium text-green-700 dark:text-green-400">All clear this week</p>
            <p className="text-sm text-muted-foreground mt-1">No critical actions identified. Check back after the next auto-scan.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {actions.map((action, idx) => {
            const colors = urgencyColor(action.totalScore);
            const label  = urgencyLabel(action.totalScore);

            // Find the primary CTA (highest-scoring reason with a CTA)
            const primaryCta = action.reasons
              .filter(r => r.cta)
              .sort((a, b) => b.score - a.score)[0]?.cta;

            return (
              <Card key={action.org_id} className="relative overflow-hidden">
                {/* Urgency stripe */}
                <div className={`absolute left-0 top-0 bottom-0 w-1 ${colors.dot}`} />

                <CardContent className="pl-5 pt-4 pb-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      {/* Org + rank */}
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs font-semibold text-muted-foreground w-5 shrink-0">#{idx + 1}</span>
                        <p className="font-semibold text-sm truncate">{action.org_name}</p>
                        <Badge variant="outline" className={`text-xs shrink-0 ${colors.badge}`}>
                          {label}
                        </Badge>
                        <span className="text-xs text-muted-foreground shrink-0">{action.totalScore} pts</span>
                      </div>

                      {/* Reasons */}
                      <div className="space-y-1.5 ml-7">
                        {action.reasons.map((reason, rIdx) => {
                          const Icon = reason.icon;
                          return (
                            <div key={rIdx} className="flex items-start gap-2">
                              <Icon className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" />
                              <div className="min-w-0">
                                <span className="text-sm font-medium">{reason.label}</span>
                                {reason.detail && (
                                  <span className="text-xs text-muted-foreground ml-1.5">{reason.detail}</span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Action buttons */}
                    <div className="flex flex-col gap-1.5 shrink-0">
                      {primaryCta && (
                        <Button size="sm" className="h-8 text-xs" onClick={() => navigate(primaryCta.path)}>
                          {primaryCta.label}
                          <ArrowRight className="h-3 w-3 ml-1" />
                        </Button>
                      )}
                      {action.reasons.filter(r => r.cta && r.cta !== primaryCta).slice(0, 1).map((r, i) => (
                        <Button
                          key={i} size="sm" variant="outline" className="h-8 text-xs"
                          onClick={() => navigate(r.cta!.path)}
                        >
                          {r.cta!.label}
                        </Button>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Legend */}
      <div className="border rounded-lg p-4 space-y-2 bg-muted/20">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">How urgency is scored</p>
        <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs text-muted-foreground">
          <div className="flex items-center gap-2"><AlertTriangle className="h-3 w-3 text-red-500" /> High health flag = {SCORE.HIGH_FLAG} pts</div>
          <div className="flex items-center gap-2"><TrendingDown className="h-3 w-3 text-orange-500" /> Dropping velocity = {SCORE.DROPPING_VELOCITY} pts</div>
          <div className="flex items-center gap-2"><Brain className="h-3 w-3 text-primary" /> Pending thinking session = {SCORE.THINKING_PENDING} pts</div>
          <div className="flex items-center gap-2"><AlertTriangle className="h-3 w-3 text-yellow-500" /> Medium health flag = {SCORE.MEDIUM_FLAG} pts</div>
          <div className="flex items-center gap-2"><MessageSquare className="h-3 w-3" /> Comms gap (14d) = {SCORE.COMMS_GAP} pts</div>
          <div className="flex items-center gap-2"><TrendingDown className="h-3 w-3 text-yellow-500" /> Low activation = {SCORE.LOW_ACTIVATION} pts</div>
          <div className="flex items-center gap-2"><BookOpen className="h-3 w-3" /> Knowledge not set up = {SCORE.KNOWLEDGE_INCOMPLETE} pts</div>
          <div className="flex items-center gap-2"><Clock className="h-3 w-3" /> Critical ≥ 20 · High ≥ 12 · Medium ≥ 6</div>
        </div>
      </div>
    </div>
  );
}
